import { useContext, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID } from '../../constants'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/modalProvider'

import { useNavigate } from '../../hooks/useNavigate'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { Button } from '../../components/button'
import { Card } from '../../components/card/Card'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Input } from '../../components/input'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { DetailContent } from '../../components/pagingList'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { RowData } from './components'
import { CalculateConfirmModal } from './modals/CalculateConfirmModal'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'
import { ReactComponent as ArrowDownRoundIcon } from '../../assets/ico/common/expand/expand_roundDown.svg'
import { ReactComponent as ArrowDownIcon } from '../../assets/ico/common/expand/expand_down.svg'
import { ReactComponent as CheckGrayIcon } from '../../assets/ico/common/check/check_default_gray_line.svg'
import { ReactComponent as CheckBlueIcon } from '../../assets/ico/common/check/check_checked_blue_line.svg'

interface Accordion {
  sale: boolean
  amount: boolean
}

interface Agreed {
  procedure: boolean
  personalProvide: boolean
  duty: boolean
}

interface Rrn {
  value: string
  masking: string
}

export const ProductCalculate = observer(() => {
  const {
    commonStore: { setInvalidAccess },
    authStore: { userInfo },
    myStore,
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)
  const detailStore = myStore.purchaseDetailStore
  const disposalStore = myStore.productDisposalStore

  const [accordion, setAccordion] = useState<Accordion>({ sale: false, amount: false })
  const [agreed, setAgreed] = useState<Agreed>({
    procedure: false,
    personalProvide: false,
    duty: false,
  })
  const [rrn, setRrn] = useState<Rrn>({ value: '', masking: '' })

  const navigate = useNavigate()
  const location = useLocation()

  const userProductId = Number(location.pathname.split('/')[3])

  // 과세대상금액
  const otherIncome =
    typeof disposalStore.content?.otherIncome === 'number' && disposalStore.content.otherIncome >= 50000
      ? disposalStore.content.otherIncome
      : 0

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const response = await detailStore.load({ param: { userProductId } }, true)

      if (
        typeof userInfo === 'undefined' ||
        userInfo?.id !== response?.userId ||
        response?.status !== 'TO_BE_SETTLEMENT'
      ) {
        setInvalidAccess(true)
        return
      }

      await disposalStore.load({ param: { userProductId } }, true)
    } catch (error) {
      console.log({ error })
      setInvalidAccess(true)
    }
  }

  const toggleSaleAccordion = () => setAccordion((prevState) => ({ ...prevState, sale: !prevState.sale }))

  const toggleAmountAccordion = () => setAccordion((prevState) => ({ ...prevState, amount: !prevState.amount }))

  const toggleAgreed = (name: keyof typeof agreed) => {
    setAgreed((prevState) => ({ ...prevState, [name]: !prevState[name] }))
  }

  const handleRrn = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const regex = /^[0-9\-\●]*$/

    if (!regex.test(value) || value.length > 14) return false

    const masking = value
      .split('')
      .map((v, i) => (i > 7 ? '●' : v))
      .join('')

    setRrn((prevState) => {
      if (prevState.value.length === 5 && value.length === 6) {
        return {
          value: `${value}-`,
          masking: `${value}-`,
        }
      }
      return {
        value:
          value.length < prevState.value.length
            ? prevState.value.slice(0, -1)
            : `${prevState.value}${value.split('').pop()}`,
        masking,
      }
    })
  }

  const calculate = () => {
    openModal({
      id: MODALS_ID.CALCULATE,
      Component: () => <CalculateConfirmModal userProductId={userProductId} success={goSettlement} />,
    })
  }

  const goSettlement = () => {
    navigate(`/myPage/asset/${userProductId}/settlement`)
  }

  return (
    <Wrap>
      <Header type="SUB" title="매각 대금 정산하기" />
      <StyledContent backgroundColor="gray_1">
        <DetailContent detailStore={detailStore}>
          <ProductInfoWrap>
            {/* 작품 정보 */}
            <ProductInfoCard>
              <ProductInfoContent $gap={16}>
                <ProductImage>
                  <img src={detailStore.content?.product.imageUri} alt={detailStore.content?.product.name} />
                </ProductImage>
                <ColumnItem $gap={4}>
                  <Text size={18} weight={600}>
                    {detailStore.content?.product.artist}
                  </Text>
                  <Text size={14} color="text_secondary">
                    {detailStore.content?.product.name}
                  </Text>
                </ColumnItem>
                <ProductDetailLink>
                  <Text size={14} weight={500} color="text_secondary">
                    작품 상세 보기
                  </Text>
                  <ArrowRightGrayIcon />
                </ProductDetailLink>
              </ProductInfoContent>
              <DivideLine />
              <AccordionWrap>
                <SaleInfoToggleBox isActive={accordion.sale}>
                  <Card $direction="column" $gap={16} $padding="16px" $radius={8} $background="gray_1">
                    <RowData
                      title="보유 수량"
                      content={{
                        value: `${detailStore.content?.totalShares?.toLocaleString()}주`,
                        size: 18,
                        weight: 600,
                      }}
                    />
                    <RowData
                      title="정산 금액"
                      content={{
                        value: `${disposalStore.content?.amount.toLocaleString()}원`,
                        size: 18,
                        weight: 600,
                      }}
                    />
                  </Card>
                  <ArrowDownRoundIcon onClick={toggleSaleAccordion} />
                </SaleInfoToggleBox>
                <Accordion isActive={accordion.sale}>
                  <ColumnItem $gap={16}>
                    <RowData
                      title="배정 확정일"
                      content={`${dayjs(detailStore.content?.product.activeProductSale?.allotmentDate).format(
                        'YYYY-MM-DD',
                      )}`}
                    />
                    <RowData
                      title="매각 완료일"
                      content={`${dayjs(detailStore.content?.product.sellHistory?.sellDate).format('YYYY-MM-DD')}`}
                    />
                    <RowData
                      title="보유일"
                      content={`${dayjs().diff(
                        detailStore.content?.product.activeProductSale?.allotmentDate,
                        'days',
                      )}일`}
                    />
                    <RowData
                      title="총 발행 금액"
                      content={`${detailStore.content?.product.activeProductSale?.totalIssuedPrice.toLocaleString()}원`}
                    />
                    <RowData title="작품 매각 가격" content={`${disposalStore.content?.actualSellPrice}원`} />
                    <RowData title="주당 공모가 액" content={`${detailStore.content?.offerPrice.toLocaleString()}원`} />
                    <RowData title="매각 단가" content={`${detailStore.content?.product.sellHistory?.sellPrice}원`} />
                    <RowData
                      title="공모가 대비 가치 상승률"
                      content={{ value: `${detailStore.content?.product.sellHistory?.revenueRate}%`, color: 'error' }}
                    />
                  </ColumnItem>
                </Accordion>
              </AccordionWrap>
            </ProductInfoCard>
            {/* 정산 금액 */}
            <ProductInfoCard>
              <RowData
                title={{ value: '취득가액', size: 18, weight: 600, color: 'text_primary' }}
                content={`${disposalStore.content?.purchaseBalance.toLocaleString()}원`}
              />
              <RowData title="양도가액" content={`${disposalStore.content?.originRevenue.toLocaleString()}원`} />
              <RowData title="기타 소득세" content={`${disposalStore.content?.otherIncomeTax.toLocaleString()}원`} />
              <RowData title="지방 소득세" content={`${disposalStore.content?.localTax.toLocaleString()}원`} />
              <DivideLine />
              <AccordionWrap>
                <RowBetween>
                  <Text size={18} weight={600}>
                    정산 금액
                  </Text>
                  <AmountInfoToggleBox isActive={accordion.amount}>
                    <Text size={18} weight={600}>{`${disposalStore.content?.amount.toLocaleString()}원`}</Text>
                    <ArrowDownIcon onClick={toggleAmountAccordion} />
                  </AmountInfoToggleBox>
                </RowBetween>
                <Accordion isActive={accordion.amount}>
                  <Card $direction="column" $gap={16} $padding="16px" $radius={16} $background="gray_1">
                    <RevenueAmountCardContent>
                      <Text size={14} weight={600} block={true}>
                        나의 양도소득 산출 내역
                      </Text>
                      <Text size={14} lineHeight={22} color="text_secondary">
                        • 양도가액: {disposalStore.content?.originRevenue.toLocaleString()}원<br />• 취득가액:{' '}
                        {disposalStore.content?.purchaseBalance.toLocaleString()}원<br />• 취득 환산가액:{' '}
                        {disposalStore.content?.calculatedBalance.toLocaleString()}원<br />• 인정 필요경비:{' '}
                        {disposalStore.content?.appliedExpense.toLocaleString()}원
                        <br />• 기타 소득 금액: {disposalStore.content?.otherIncome.toLocaleString()}원<br />• 과세대상
                        금액: {otherIncome.toLocaleString()}원
                        <br />• 기타 소득세: {disposalStore.content?.otherIncomeTax.toLocaleString()}원<br />• 지방
                        소득세: {disposalStore.content?.localTax.toLocaleString()}원
                      </Text>
                    </RevenueAmountCardContent>
                    <RevenueAmountCardContent>
                      <Text size={14} weight={600} block={true}>
                        서화, 골동품 양도소득 계산법
                      </Text>
                      <Text size={14} lineHeight={22} color="text_secondary">
                        • 양도가액: 분할 소유권 보유 수량*분할 소유권 매각 단가
                        <br />
                        • 취득가액: 분할 소유권 보유 수량*평균 취득가
                        <br />
                        • 취득 환산가액: 양도가액*인정 필요경비 비율
                        <br />
                        • 인정 필요경비: 취득가액과 취득환산개악 중 큰 금액
                        <br />
                        • 기타소득금액: 양도가액 - 수수료 - 인정 필요경비
                        <br />
                        • 과세대상 금액: 기타소득금액이 5만원 이상일시 해당
                        <br />
                        • 기타 소득세: 기타소득금액*20%(원 단위 이하 금액은 절삭)
                        <br />
                        • 지방 소득세: 기타 소득세*10
                        <br />
                        • 작품 매각 가격 1억 이하: 90%
                        <br />• 작품 매각 가격 1억 초과: (9천만원 + 1억 초과분의 80%)/매각 가격*100
                      </Text>
                    </RevenueAmountCardContent>
                  </Card>
                </Accordion>
              </AccordionWrap>
            </ProductInfoCard>
            {/* 정산 동의 */}
            <ProductInfoCard>
              <AgreedCheckbox onClick={() => toggleAgreed('procedure')}>
                {agreed.procedure ? <CheckBlueIcon /> : <CheckGrayIcon />}
                <Text size={14} lineHeight={22} color="text_secondary">
                  (필수) 매각 대금을 지급받음으로써 미술작품 매각 관련 절차가 완료되었음을 확인했어요.
                </Text>
              </AgreedCheckbox>
              <AgreedCheckbox onClick={() => toggleAgreed('personalProvide')}>
                {agreed.personalProvide ? <CheckBlueIcon /> : <CheckGrayIcon />}
                <Text size={14} lineHeight={22} color="text_secondary">
                  (필수) 소득세 신고를 위한 개인정보제공에 동의해요.
                </Text>
              </AgreedCheckbox>
              <AgreedCheckbox onClick={() => toggleAgreed('duty')}>
                {agreed.duty ? <CheckBlueIcon /> : <CheckGrayIcon />}
                <Text size={14} lineHeight={22} color="text_secondary">
                  (필수) 주민등록번호 오기입으로 인해 발생한 불이익에 대해서 회사는 책임을 부담하지 않아요.
                </Text>
              </AgreedCheckbox>
            </ProductInfoCard>
            {/* 주민등록번호 입력 */}
            <ProductInfoCard>
              <Text size={18} weight={600}>
                주민등록번호 입력
              </Text>
              <ColumnItem $gap={8}>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={rrn.masking}
                  placeholder="주민등록번호를 입력해주세요."
                  onChange={handleRrn}
                />
                <Text size={12} color="text_secondary">
                  소득세 신고를 위해 주민등록번호 기입이 필수입니다.
                </Text>
              </ColumnItem>
            </ProductInfoCard>
          </ProductInfoWrap>
          <NoticeContainer
            title="안내사항"
            list={[
              '정확한 주민등록번호를 기입해 주세요.',
              '입력하신 주민등록번호는 암호화되어 안전하게 보호되며, 소득세 신고 외에 어디에도 사용되지 않아요.',
              '입력하신 개인정보는 본인 동의 없이 제3자에게 제공되지 않아요.',
            ]}
            $backgroundColor="gray_1"
          />
        </DetailContent>
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={!Object.values(agreed).every((v) => v === true) || !(rrn.value.length === 14)}
          block={true}
          onClick={calculate}
        >
          정산하기
        </Button>
      </Footer>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const ProductInfoWrap = styled(ColumnItem)`
  gap: 16px;
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
`

const SaleInfoToggleBox = styled(ColumnItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const AccordionWrap = styled(ColumnItem)``

const Accordion = styled(ColumnItem)<{ isActive: boolean }>`
  overflow: hidden;
  max-height: ${({ isActive }) => (isActive ? '100vh' : 0)};
  transition: 0.5s;
  & > * {
    margin-top: 16px;
  }
`

const AmountInfoToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const RevenueAmountCardContent = styled(ColumnItem)`
  gap: 12px;
`

const AgreedCheckbox = styled(AlignCenterItem)`
  gap: 8px;
  > svg {
    flex-shrink: 0;
  }
`
